import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { CustomerdatabaseService } from '../../services/customerdatabase.service';
import { Workflows } from '../../communication/dashboard/workflow-helper';
import { AdvancedWorkflows } from '../../communication/dashboard/advanced-workflow-helper';
import { EnterpriseWorkflows } from '../../communication/dashboard/enterprise-workflow-helper';
import { AuthService } from '../../services/auth.service';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProfileCompletionGuard implements CanActivate {

  constructor(
    private customerDB: CustomerdatabaseService,
    private authService: AuthService,
    private router: Router
  ) { }

  async canActivate(): Promise<boolean> {
    // Check profile completion
    //wait until customerDB is ready
    await this.customerDB.isAuthenticatedOnCustDb$.pipe(filter(isAuth => isAuth), take(1)).toPromise();
    const customerNumber = String(this.customerDB.getCustomerNumber?.() || '');
    const isSelfServiceCustomer = this.customerDB.isSelfServiceMode();
    if (isSelfServiceCustomer) {
      try {
        // Sync default contact workflows for self-service to the booked level
        // in branding.plan.recruiting ({ interview, 1call, 2call, 3call }):
        // - create missing ones the plan now allows
        // - delete seed-named workflows the plan no longer allows (downgrade)
        // Only seed names are touched; customer-created workflows are safe.
        console.log('🚚 ProfileCompletionGuard: Self-Service contact workflow sync starting...');
        const brandingSnap = await this.customerDB.getProfile().get();
        const brandingData: any = brandingSnap.docs[0]?.data() ?? null;
        const planRecruiting = brandingData?.plan?.recruiting;
        console.log('🧾 ProfileCompletionGuard: Branding doc found:', !!brandingData, 'recruiting config present:', !!planRecruiting);

        const desiredNames = this.getDesiredContactWorkflowNames(planRecruiting);
        console.log('🎯 ProfileCompletionGuard: Desired contact workflow names:', Array.from(desiredNames));

        // Names owned by the self-service seed (workflow-helper.ts). Only these
        // names are candidates for automatic delete on downgrade.
        const seedOwnedNames = new Set<string>(Workflows.wfs.map(w => w.name));

        // Load existing workflows once, indexed by name, so we can diff against
        // desiredNames for both create and delete passes.
        const allExistingSnap = await this.customerDB.getWorkflows().get();
        const existingByName = new Map<string, string>(); // name -> doc id
        for (const doc of allExistingSnap.docs) {
          const name = (doc.data() as any)?.name;
          if (typeof name === 'string' && name) existingByName.set(name, doc.id);
        }
        console.log('📋 ProfileCompletionGuard: Existing workflow names:', Array.from(existingByName.keys()));

        // Delete pass: seed-owned workflows the plan no longer allows (downgrade).
        for (const [name, docId] of existingByName.entries()) {
          if (!seedOwnedNames.has(name)) continue; // leave customer-created workflows alone
          if (desiredNames.has(name)) continue;    // still allowed
          console.log('🗑️  ProfileCompletionGuard: Deleting seed workflow not allowed by plan:', name, 'id:', docId);
          try {
            await this.customerDB.app2.firestore.collection('workflows').doc(docId).delete();
            existingByName.delete(name);
            console.log('🧹 ProfileCompletionGuard: Deleted seed workflow:', name);
          } catch (delErr) {
            console.error('❌ ProfileCompletionGuard: Failed to delete seed workflow:', name, delErr);
          }
        }

        // Create pass: seed-owned workflows the plan allows but that do not exist yet.
        for (const wf of Workflows.wfs) {
          if (!desiredNames.has(wf.name)) {
            console.log('⏭️  ProfileCompletionGuard: Skipping workflow (not desired by plan):', wf.name);
            continue;
          }
          if (existingByName.has(wf.name)) {
            console.log('✔️  ProfileCompletionGuard: Contact workflow already exists, skipping:', wf.name);
            continue;
          }
          console.log('➕ ProfileCompletionGuard: Creating contact workflow:', wf.name);
          await this.customerDB.createWorkflow(wf);
          console.log('✨ ProfileCompletionGuard: Contact workflow created:', wf.name);
        }
        console.log('🚚 ProfileCompletionGuard: Self-Service contact workflow sync done.');

        // Always ensure the three specific workflows exist
        console.log('🔧 ProfileCompletionGuard: Ensuring advanced workflows exist...');
        const customerEmail = this.authService.returnUser?.() || '';
        const customerEmails = customerEmail ? [customerEmail] : [];
        console.log('📧 ProfileCompletionGuard: Customer email:', customerEmail);

        const allWfsSnap = await this.customerDB.getWorkflows().get();
        const allWfs = allWfsSnap.docs.map(d => d.data() as any);
        console.log('📋 ProfileCompletionGuard: Found', allWfs.length, 'existing workflows');

        console.log('🎯 ProfileCompletionGuard: Checking AdvancedWorkflows.wfs:', AdvancedWorkflows.wfs.length, 'workflows');

        // First pass: create workflows and collect their IDs
        const createdWorkflowIds = new Map<string, string>();

        for (const base of AdvancedWorkflows.wfs) {
          console.log('🔍 ProfileCompletionGuard: Checking workflow:', base.name, 'dothis:', base.dothis, 'dynamicAttachment:', base.dynamicAttachment);

          const exists = allWfs.some(w => {
            if (base.dothis === 'email') {
              const match = w.dothis === 'email' && w.dynamicAttachment === base.dynamicAttachment;
              console.log('📧 ProfileCompletionGuard: Email workflow check - existing:', w.name, 'dothis:', w.dothis, 'dynamicAttachment:', w.dynamicAttachment, 'match:', match);
              return match;
            }
            if (base.dothis === 'calendly') {
              const match = w.dothis === 'calendly';
              console.log('📅 ProfileCompletionGuard: Calendly workflow check - existing:', w.name, 'dothis:', w.dothis, 'match:', match);
              return match;
            }
            const match = w.name === base.name;
            console.log('📝 ProfileCompletionGuard: Name workflow check - existing:', w.name, 'base:', base.name, 'match:', match);
            return match;
          });

          console.log('✅ ProfileCompletionGuard: Workflow', base.name, 'exists:', exists);

          if (!exists) {
            console.log('➕ ProfileCompletionGuard: Creating missing workflow:', base.name);

            // Template mapping for workflows with documents
            const templateMapping = new Map([
              ['Matching SeSe', { filename: '1757344145098_Selfservice_Profil-Teaser.docx', displayName: 'Profil Teaser Demo' }],
              ['Profil SeSe', { filename: '1757344326152_Selfservice_Profil-LANG.docx', displayName: 'Profil Lang Demo' }]
            ]);

            const wfToCreate = {
              ...base,
              emails: customerEmails,
              receivers: customerEmails,
              calendlyLink: base.dothis === 'calendly' ? '' : (base as any).calendlyLink || '',
            } as any;

            // Upload template if this workflow has one
            if (templateMapping.has(base.name)) {
              const template = templateMapping.get(base.name);
              console.log('📤 ProfileCompletionGuard: Uploading template for workflow:', base.name, 'file:', template.filename);

              try {
                // Load the local .docx file from assets/templates folder
                const templatePath = `assets/templates/${template.filename}`;
                console.log('📂 ProfileCompletionGuard: Loading template from:', templatePath);

                const response = await fetch(templatePath);
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }

                const blob = await response.blob();
                console.log('📦 ProfileCompletionGuard: Template loaded, size:', blob.size, 'bytes');

                // Create a File object with proper MIME type for .docx
                const file = new File([blob], template.displayName + '.docx', {
                  type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                });
                console.log('📋 ProfileCompletionGuard: Created file object:', file.name, 'type:', file.type);

                // Upload the file using the system's upload mechanism
                const fileStoragePath = `filesStorage/${Date.now()}_${file.name}`;
                const uploadTask = this.customerDB.getCustomerUploadTask(fileStoragePath, file);
                const uploadResult = await uploadTask;

                // Get the download URL from the upload result
                const downloadURL = await uploadResult.ref.getDownloadURL();
                console.log('✨ ProfileCompletionGuard: Successfully uploaded template, URL:', downloadURL);

                // Create entry in vorlagen collection
                try {
                  const vorlageData = {
                    created: new Date(),
                    createdISO: new Date().toISOString(),
                    downloadURL: downloadURL,
                    name: file.name
                  };

                  await this.customerDB.app2.firestore
                    .collection('vorlagen')
                    .add(vorlageData);

                  console.log('📋 ProfileCompletionGuard: Created vorlage entry for:', template.displayName);
                } catch (vorlageError) {
                  console.error('❌ ProfileCompletionGuard: Failed to create vorlage entry for:', template.displayName, vorlageError);
                }

                // Update workflow with document information
                wfToCreate.documentURL = downloadURL;
                wfToCreate.documentName = template.displayName;

              } catch (error) {
                console.error('❌ ProfileCompletionGuard: Failed to upload template for:', base.name, error);
              }
            }

            console.log('🏗️ ProfileCompletionGuard: Workflow to create:', JSON.stringify(wfToCreate, null, 2));
            const docRef = await this.customerDB.createWorkflow(wfToCreate);
            const workflowId = docRef.id;
            createdWorkflowIds.set(base.name, workflowId);
            console.log('✨ ProfileCompletionGuard: Successfully created workflow:', base.name, 'with ID:', workflowId);
          }
        }

        // Second pass: Update Matching SeSe to include Profil SeSe as subworkflow
        if (createdWorkflowIds.has('Matching SeSe') && createdWorkflowIds.has('Profil SeSe')) {
          const matchingId = createdWorkflowIds.get('Matching SeSe');
          const profilId = createdWorkflowIds.get('Profil SeSe');
          console.log('🔗 ProfileCompletionGuard: Linking Profil SeSe (' + profilId + ') as subworkflow of Matching SeSe (' + matchingId + ')');

          await this.customerDB.app2.firestore
            .collection('workflows')
            .doc(matchingId)
            .update({
              subWorkflows: [profilId],
              updated: new Date(),
              updatedGerman: new Date().toLocaleDateString('de-DE'),
              updatedISO: new Date().toISOString(),
            });
          console.log('✅ ProfileCompletionGuard: Successfully linked Profil SeSe as subworkflow of Matching SeSe');
        }

        console.log('✅ ProfileCompletionGuard: All advanced workflows processed successfully');

        console.log('🎉 ProfileCompletionGuard: Advanced workflows check completed');
      } catch (e) {
        console.warn('ProfileCompletionGuard: failed to ensure default workflows', e);
      }
      return this.checkProfileCompletion();
    }

    // For Enterprise customers (non-self-service), ensure Enterprise workflows exist ONLY for new customers
    const isEnterpriseCustomer = !!customerNumber && customerNumber !== '0' && customerNumber !== '100002' && customerNumber !== '100001';
    if (!isSelfServiceCustomer && isEnterpriseCustomer) {
      try {
        console.log('🏢 ProfileCompletionGuard: Checking if Enterprise customer needs initial workflows for customer:', customerNumber);

        const allWfsSnap = await this.customerDB.getWorkflows().get();
        const allWfs = allWfsSnap.docs.map(d => d.data() as any);
        console.log('📋 ProfileCompletionGuard: Found', allWfs.length, 'existing workflows for Enterprise customer');

        // IMPORTANT: Only create workflows for NEW customers (no workflows exist yet)
        // This prevents overwriting existing customer workflows and disrupting running processes
        if (allWfs.length === 0) {
          console.log('🆕 ProfileCompletionGuard: NEW Enterprise customer detected - creating initial workflows');
          const customerEmail = this.authService.returnUser?.() || '';
          const customerEmails = ['hendrik.terwort@mona-ai.de']; // Fixed recipient for Enterprise workflows
          console.log('📧 ProfileCompletionGuard: Enterprise customer email:', customerEmail);

          console.log('🎯 ProfileCompletionGuard: Creating EnterpriseWorkflows.wfs:', EnterpriseWorkflows.wfs.length, 'workflows');

          // Create all Enterprise workflows for new customer (same logic as Self-Service)
          const createdWorkflowIds = new Map<string, string>();

          // Template mapping for Enterprise workflows with pre-uploaded documents
          const enterpriseTemplateMapping = new Map([
            ['Teaserprofil und Matching', {
              displayName: 'Teaser-Profil und Matching-Analyse',
              downloadURL: 'https://firebasestorage.googleapis.com/v0/b/mona-recruiting-instance2.firebasestorage.app/o/filesStorage%2FProfil-Teaser_Vorlage%201.docx?alt=media&token=37b17f37-1a2a-4e7c-9a58-ec84d1ad03d2'
            }],
            ['Profil lang', {
              displayName: 'Ausführliches Bewerberprofil',
              downloadURL: 'https://firebasestorage.googleapis.com/v0/b/mona-recruiting-instance2.firebasestorage.app/o/filesStorage%2FProfil-LANG_Vorlage%201.docx?alt=media&token=4ef8ea30-74de-4db5-b33d-eb84eac5cd99'
            }]
          ]);

          // Available template: Vorlage Vertriebsemail 1.docx
          // URL: https://firebasestorage.googleapis.com/v0/b/mona-recruiting-instance2.firebasestorage.app/o/filesStorage%2FVorlage%20Vertriebsemail%201.docx?alt=media&token=3e805684-0e84-4ba4-a1b6-3cd7f84103a9

          // Use pre-uploaded template URLs (no runtime upload needed due to strict Firebase rules)
          const templateUrls = new Map<string, { downloadURL: string, displayName: string }>();

          // Set the pre-uploaded URLs directly
          for (const [workflowName, template] of enterpriseTemplateMapping) {
            templateUrls.set(workflowName, {
              downloadURL: template.downloadURL,
              displayName: template.displayName
            });

            // Create entry in vorlagen collection for pre-uploaded templates
            if (template.downloadURL.startsWith('https://firebasestorage.googleapis.com')) {
              try {
                const vorlageData = {
                  created: new Date(),
                  createdISO: new Date().toISOString(),
                  downloadURL: template.downloadURL,
                  name: template.displayName + '.docx',
                  filename: template.displayName + '.docx'
                };

                await this.customerDB.getContracts().add(vorlageData);
                console.log('📋 ProfileCompletionGuard: Created vorlage entry for pre-uploaded template:', template.displayName);
              } catch (vorlageError) {
                console.error('❌ ProfileCompletionGuard: Failed to create vorlage entry:', template.displayName, vorlageError);
              }
            }

            console.log('🔗 ProfileCompletionGuard: Using pre-uploaded template:', workflowName, 'URL:', template.downloadURL);
          }

          // Second: Create workflows with uploaded template URLs
          for (const base of EnterpriseWorkflows.wfs) {
            console.log('➕ ProfileCompletionGuard: Creating Enterprise workflow for new customer:', base.name);

            const wfToCreate = {
              ...base,
              emails: customerEmails,
              receivers: customerEmails,
              calendlyLink: base.dothis === 'calendly' ? '' : (base as any).calendlyLink || '',
            } as any;

            // Set template URL if this workflow has one
            if (templateUrls.has(base.name)) {
              const templateInfo = templateUrls.get(base.name);
              wfToCreate.documentURL = templateInfo.downloadURL;
              wfToCreate.documentName = templateInfo.displayName;
              console.log('🔗 ProfileCompletionGuard: Set template URL for workflow:', base.name, 'URL:', templateInfo.downloadURL);
            }

            console.log('🏗️ ProfileCompletionGuard: Enterprise workflow to create:', JSON.stringify(wfToCreate, null, 2));
            const docRef = await this.customerDB.createWorkflow(wfToCreate);
            const workflowId = docRef.id;
            createdWorkflowIds.set(base.name, workflowId);
            console.log('✨ ProfileCompletionGuard: Successfully created Enterprise workflow:', base.name, 'with ID:', workflowId);
          }

          // Second pass: Update Teaserprofil und Matching to include Profil lang as subworkflow
          if (createdWorkflowIds.has('Teaserprofil und Matching') && createdWorkflowIds.has('Profil lang')) {
            const matchingId = createdWorkflowIds.get('Teaserprofil und Matching');
            const profilId = createdWorkflowIds.get('Profil lang');
            console.log('🔗 ProfileCompletionGuard: Linking Profil lang (' + profilId + ') as subworkflow of Teaserprofil und Matching (' + matchingId + ')');

            await this.customerDB.app2.firestore
              .collection('workflows')
              .doc(matchingId)
              .update({
                subWorkflows: [profilId],
                updated: new Date(),
                updatedGerman: new Date().toLocaleDateString('de-DE'),
                updatedISO: new Date().toISOString(),
              });
            console.log('✅ ProfileCompletionGuard: Successfully linked Profil lang as subworkflow of Teaserprofil und Matching');
          }

          console.log('✅ ProfileCompletionGuard: All Enterprise workflows processed successfully');

          console.log('🎉 ProfileCompletionGuard: Initial Enterprise workflows created for new customer');
        } else {
          console.log('👤 ProfileCompletionGuard: EXISTING Enterprise customer detected - skipping workflow creation to preserve existing processes');
        }

      } catch (error) {
        console.error('Error in ProfileCompletionGuard for Enterprise customer:', error);
      }
    }

    // For all other customers allow navigation normally
    return true;
  }

  /**
   * Decide which default contact workflow names from Workflows.wfs should be
   * seeded for a self-service customer, based on branding.plan.recruiting.
   * - interview (or all call flags false): no contact workflows at all
   * - 1call: SMS, PHONE, EMAIL (direkt)
   * - 2call: + EMAIL4H
   * - 3call: + Email24H
   * - Fallback when plan.recruiting is missing: legacy behavior (create all).
   */
  public getDesiredContactWorkflowNames(planRecruiting: any): Set<string> {
    if (!planRecruiting) {
      console.log('🧭 getDesiredContactWorkflowNames: planRecruiting missing -> legacy fallback (all 5 contact workflows)');
      return new Set<string>(['SMS', 'EMAIL', 'EMAIL4H', 'Email24H', 'PHONE']);
    }
    console.log('🧭 getDesiredContactWorkflowNames: input flags:',
      'interview=', planRecruiting.interview,
      "'1call'=", planRecruiting['1call'],
      "'2call'=", planRecruiting['2call'],
      "'3call'=", planRecruiting['3call']);

    // Call flags are cumulative (2calls -> 1call+2call true, 3calls -> all three true).
    // The `interview` flag is set to true on most plans (backend sets it as
    // "interview feature included"), so we only treat it as "interview-only" when
    // no call flag is active. Call flags therefore win over `interview`.
    const hasAnyCall = !!(planRecruiting['1call'] || planRecruiting['2call'] || planRecruiting['3call']);
    if (!hasAnyCall) {
      console.log('🧭 getDesiredContactWorkflowNames: no call level active (interview-only plan) -> empty set');
      return new Set<string>();
    }
    const names = new Set<string>(['SMS', 'PHONE', 'EMAIL']);
    if (planRecruiting['2call'] || planRecruiting['3call']) names.add('EMAIL4H');
    if (planRecruiting['3call']) names.add('Email24H');
    console.log('🧭 getDesiredContactWorkflowNames: resolved names:', Array.from(names));
    return names;
  }

  private checkProfileCompletion(): Promise<boolean> {
    // Do not redirect anymore; allow navigation and let dashboard handle modal
    return this.getCompletionStatus()
      .then(() => true)
      .catch(() => true);
  }

  /**
   * Single source of truth for completion state
   */
  private async getCompletionStatus(): Promise<{
    hasCompanyName: boolean;
    hasCompanyLogo: boolean;
    hasJobOffer: boolean;
    hasOnboardingEmail: boolean;
    isComplete: boolean;
    nextStep: number;
  }> {
    console.log('🔍 ProfileCompletionGuard: Starting profile check...');
    const snapshot = await this.customerDB.getProfile().get();

    if (snapshot.docs.length === 0) {
      console.log('❌ ProfileCompletionGuard: No branding profile found');
      return {
        hasCompanyName: false,
        hasCompanyLogo: false,
        hasJobOffer: false,
        hasOnboardingEmail: false,
        isComplete: false,
        nextStep: 1,
      };
    }

    const profileData: any = snapshot.docs[0].data();

    const hasCompanyName = this.isValidField(profileData.firmenname);
    const hasCompanyLogo = this.hasValidLogo(profileData.companyLogo);

    // Interview is not needed anymore → keep computing values for debug/compat, but ignore in completion
    let hasJobOffer = false;
    try {
      const processed = await this.customerDB.getJobOffersFromAPI();
      hasJobOffer = Array.isArray(processed.data) && processed.data.length > 0;
    } catch (e) {
      console.warn('ProfileCompletionGuard: Failed to load job offers, assuming none.');
      hasJobOffer = false;
    }

    const imapArray = (profileData as any)?.imapArray;
    const hasOnboardingEmail = Array.isArray(imapArray) && imapArray.length > 0;

    const isComplete = hasCompanyName && hasCompanyLogo && hasJobOffer && hasOnboardingEmail;
    const nextStep = !hasCompanyName ? 1 : (!hasCompanyLogo ? 2 : (!hasJobOffer ? 3 : 4));

    console.log('🔍 ProfileCompletionGuard status:', {
      hasCompanyName,
      hasCompanyLogo,
      hasJobOffer,
      hasOnboardingEmail
    });

    return { hasCompanyName, hasCompanyLogo, hasJobOffer, hasOnboardingEmail, isComplete, nextStep };
  }

  // Removed duplicate isProfileComplete(profileData). Unified in getCompletionStatus()

  /**
   * Validates that a field is not null, undefined, empty string, or whitespace-only
   */
  private isValidField(value: any): boolean {
    // Check for null or undefined
    if (value === null || value === undefined) {
      return false;
    }

    // Must be a string
    if (typeof value !== 'string') {
      return false;
    }

    // Check for empty string
    if (value === '') {
      return false;
    }

    // Check for whitespace-only string
    if (value.trim() === '') {
      return false;
    }

    // Must have at least 1 non-whitespace character
    return value.trim().length > 0;
  }

  /**
   * Accept logo as valid if it's a non-empty string (URL or base64)
   * or an object with a non-empty 'url' string
   */
  private hasValidLogo(logo: any): boolean {
    if (logo === null || logo === undefined) return false;
    if (typeof logo === 'string') return logo.trim().length > 0;
    if (typeof logo === 'object') {
      const url = (logo as any).url;
      return typeof url === 'string' && url.trim().length > 0;
    }
    return false;
  }

  // Removed duplicate getValidationDetails().

  /**
   * Get the specific reason why validation failed
   */
  private getValidationFailureReason(value: any): string {
    if (value === null) return 'Value is null';
    if (value === undefined) return 'Value is undefined';
    if (typeof value !== 'string') return `Value is not a string (type: ${typeof value})`;
    if (value === '') return 'Value is empty string';
    if (value.trim() === '') return 'Value contains only whitespace';
    return 'Value is valid';
  }

  /**
   * Public method to check profile completion for use in services
   */
  public async isProfileCompleteAsync(): Promise<boolean> {
    try {
      if (!this.customerDB.isSelfServiceMode()) {
        return true;
      }
      const status = await this.getCompletionStatus();
      return status.isComplete;
    } catch (error) {
      console.error('Error checking profile completion:', error);
      return false;
    }
  }
}